import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInWithEmailAndPassword, 
    GoogleAuthProvider, 
    signInWithPopup, 
    onAuthStateChanged, 
    browserLocalPersistence, 
    setPersistence 
} from 'firebase/auth';
import { adminApi } from './api';

// Minimal Firebase config
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: "iknow-admin"
};

// Simple Firebase initialization
let auth = null;
export const getFirebaseAuth = () => {
    if (!auth) {
        const app = initializeApp(firebaseConfig);
        auth = getAuth(app);
        
        // Configure persistence
        setPersistence(auth, browserLocalPersistence);
        
        // Set up auth state listener
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Get a fresh token whenever auth state changes
                try {
                    const idToken = await user.getIdToken(true);  // Force refresh
                    localStorage.setItem('adminToken', idToken);
                } catch (error) {
                    console.error('Error refreshing token:', error);
                    localStorage.removeItem('adminToken');
                    window.location.href = '/login';
                }
            } else {
                localStorage.removeItem('adminToken');
            }
        });
    }
    return auth;
};

// Initialize auth on module load
getFirebaseAuth();

export const authService = {
    login: async (email, password) => {
        try {
            console.log('Attempting Firebase email/password login...');
            const authInstance = getFirebaseAuth();
            const userCredential = await signInWithEmailAndPassword(authInstance, email, password);
            const idToken = await userCredential.user.getIdToken();
            
            // Verify with our backend at api.get-code.pro
            const response = await adminApi.login({ idToken });
            localStorage.setItem('adminToken', idToken);
            localStorage.setItem('lastAdminEmail', email);
            
            return {
                user: response.data.user,
                token: idToken
            };
        } catch (error) {
            console.error('Login error:', error);
            localStorage.removeItem('adminToken');
            throw error;
        }
    },

    loginWithGoogle: async () => {
        try {
            console.log('Attempting Firebase Google login...');
            const authInstance = getFirebaseAuth();
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            
            const userCredential = await signInWithPopup(authInstance, provider);
            const idToken = await userCredential.user.getIdToken();
            const email = userCredential.user.email;
            
            // Verify with backend
            const response = await adminApi.login({ idToken });
            localStorage.setItem('adminToken', idToken);
            if (email) {
                localStorage.setItem('lastAdminEmail', email);
            }
            
            return {
                user: response.data.user,
                token: idToken
            };
        } catch (error) {
            console.error('Google Sign-in error:', error);
            localStorage.removeItem('adminToken');
            throw error;
        }
    },

    /**
     * Check if Face ID / Touch ID / Platform WebAuthn is supported on this browser
     */
    isBiometricsAvailable: async () => {
        if (typeof window !== 'undefined' && 
            window.PublicKeyCredential && 
            window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
            try {
                return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            } catch (e) {
                return false;
            }
        }
        return false;
    },

    /**
     * Check if Face ID / Touch ID has already been registered on this device
     */
    isBiometricsConfigured: () => {
        return Boolean(localStorage.getItem('adminBiometricCredId'));
    },

    getBiometricEmail: () => {
        return localStorage.getItem('adminBiometricEmail') || localStorage.getItem('lastAdminEmail') || '';
    },

    /**
     * Register Face ID / Touch ID passkey for this device
     */
    registerBiometrics: async (email) => {
        if (!window.PublicKeyCredential) {
            throw new Error('Biometric authentication (Face ID / Touch ID) is not supported on this browser.');
        }

        const userEmail = email || localStorage.getItem('lastAdminEmail') || 'admin@get-code.pro';
        const challenge = window.crypto.getRandomValues(new Uint8Array(32));
        const userId = new TextEncoder().encode(userEmail);

        const credential = await navigator.credentials.create({
            publicKey: {
                challenge,
                rp: {
                    name: 'Get-Code Pro Admin',
                    id: window.location.hostname
                },
                user: {
                    id: userId,
                    name: userEmail,
                    displayName: userEmail
                },
                pubKeyCredParams: [
                    { alg: -7, type: 'public-key' },  // ES256
                    { alg: -257, type: 'public-key' } // RS256
                ],
                authenticatorSelection: {
                    authenticatorAttachment: 'platform', // Triggers Apple Face ID / Touch ID
                    userVerification: 'required'
                },
                timeout: 60000
            }
        });

        if (credential) {
            const rawIdBytes = new Uint8Array(credential.rawId);
            let binary = '';
            for (let i = 0; i < rawIdBytes.byteLength; i++) {
                binary += String.fromCharCode(rawIdBytes[i]);
            }
            const credIdB64 = btoa(binary);

            localStorage.setItem('adminBiometricCredId', credIdB64);
            localStorage.setItem('adminBiometricEmail', userEmail);
            return true;
        }

        return false;
    },

    /**
     * Authenticate using Face ID / Touch ID
     */
    loginWithBiometrics: async () => {
        const credIdB64 = localStorage.getItem('adminBiometricCredId');
        const email = localStorage.getItem('adminBiometricEmail') || localStorage.getItem('lastAdminEmail');

        if (!credIdB64) {
            throw new Error('Face ID is not registered on this device yet. Sign in with Google or Email once to pair Face ID.');
        }

        // Convert base64 cred ID back to ArrayBuffer
        const binary = atob(credIdB64);
        const credIdBytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            credIdBytes[i] = binary.charCodeAt(i);
        }

        const challenge = window.crypto.getRandomValues(new Uint8Array(32));

        // Invoke native Apple Face ID / Touch ID prompt
        const assertion = await navigator.credentials.get({
            publicKey: {
                challenge,
                allowCredentials: [{
                    id: credIdBytes.buffer,
                    type: 'public-key'
                }],
                userVerification: 'required',
                timeout: 60000
            }
        });

        if (!assertion) {
            throw new Error('Biometric recognition cancelled or not recognized.');
        }

        // Face ID verified! Retrieve / refresh the session token
        const authInstance = getFirebaseAuth();
        let currentUser = authInstance.currentUser;

        if (!currentUser) {
            // Wait up to 1.5s for cached Firebase credential to populate
            await new Promise((resolve) => {
                const unsub = onAuthStateChanged(authInstance, (u) => {
                    unsub();
                    currentUser = u;
                    resolve(u);
                });
                setTimeout(resolve, 1500);
            });
        }

        if (currentUser) {
            const idToken = await currentUser.getIdToken(true);
            const response = await adminApi.login({ idToken });
            localStorage.setItem('adminToken', idToken);
            return {
                user: response.data.user,
                token: idToken
            };
        } else {
            // If Firebase local token has expired or is absent, prompt a one-time sign in
            throw new Error('Session credentials expired on this device. Sign in with Google or password once to restore Face ID pairing.');
        }
    },

    disableBiometrics: () => {
        localStorage.removeItem('adminBiometricCredId');
        localStorage.removeItem('adminBiometricEmail');
    },

    logout: async () => {
        try {
            const token = localStorage.getItem('adminToken');
            if (token) {
                await adminApi.post('/api/admin/auth/logout', { token });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('adminToken');
            window.location.href = '/login';
        }
    },

    isAuthenticated: () => {
        return !!localStorage.getItem('adminToken');
    },

    refreshToken: async () => {
        try {
            const authInstance = getFirebaseAuth();
            
            const waitForUser = new Promise((resolve, reject) => {
                const unsubscribe = onAuthStateChanged(authInstance, (user) => {
                    unsubscribe();
                    if (user) {
                        resolve(user);
                    } else {
                        reject(new Error('No user signed in'));
                    }
                });
            });
            
            const currentUser = await waitForUser;
            const idToken = await currentUser.getIdToken(true);
            
            await adminApi.post('/api/admin/auth/login', { 
                idToken,
                isTokenRefresh: true 
            });
            
            localStorage.setItem('adminToken', idToken);
            return idToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            localStorage.removeItem('adminToken');
            throw error;
        }
    }
};

export default authService;